import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface Manga {
  id: string;
  title: string;
  description: string;
  coverUrl: string;
  author: string;
  status: string;
  year: number | null;
  genres: string[];
}

const MangaTile: React.FC = () => {
  const [currentlyReading] = useState<Manga>({
    id: '7169795c-7df6-48bf-9cb2-d75f664ec858',
    title: "Ichinose-ke no Taizai",
    description: "Tsubasa Ichinose is a middle schooler who loses his memory in an accident. He is able to safely reunite with his family, but is soon faced with a new shocking reality... What is going on with this family?!",
    coverUrl: '/content/manga covers/Ichinose-ke no Taizai.jpg',
    author: 'Taizan5',
    status: 'completed',
    year: 2022,
    genres: ['Psychological', 'Drama', 'Mystery']
  });
  const [recommendations] = useState<Manga[]>([
    {
      id: '43078ddf-c2aa-415d-bccb-97e35a3755fa',
      title: 'My Dearest Self With Malice Aforethought',
      description: 'A psychological thriller exploring the depths of human nature...',
      coverUrl: '/content/manga covers/Shinai naru Boku e Satsui wo Komete.jpg',
      author: 'Inoryu Hajime',
      status: 'ongoing',
      year: 2020,
      genres: ['Psychological', 'Thriller', 'Mystery']
    },
    {
      id: '4301d363-ee02-43f4-ae24-4cbf29a74830',
      title: 'Goodnight Punpun',
      description: 'A coming-of-age story that explores the darkness of adolescence...',
      coverUrl: '/content/manga covers/Goodnight Punpun.jpg',
      author: 'Asano Inio',
      status: 'completed',
      year: 2007,
      genres: ['Drama', 'Psychological', 'Slice of Life']
    },
    {
      id: 'a3e48f50-d3d1-4d08-b54a-a3b7ad4b0801',
      title: 'Yaiteru Futari',
      description: 'A wholesome cooking manga about a couple bonding over food...',
      coverUrl: '/content/manga covers/Yaiteru Futari.jpg',
      author: 'Suzumi',
      status: 'ongoing',
      year: 2021,
      genres: ['Romance', 'Slice of Life', 'Food']
    }
  ]);
  const [loading] = useState<boolean>(false);
  const [hoveredManga, setHoveredManga] = useState<Manga | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number } | null>(null);
  const [isCurrentReadingHovered, setIsCurrentReadingHovered] = useState<boolean>(false);
  const [currentReadingPopupPosition, setCurrentReadingPopupPosition] = useState<{ top: number; left: number } | null>(null);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const autoCloseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const hoverDelayTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const currentReadingCloseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (manga: Manga, event: React.MouseEvent<HTMLDivElement>) => {
    // Clear any existing timeouts
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    if (hoverDelayTimeoutRef.current) {
      clearTimeout(hoverDelayTimeoutRef.current);
      hoverDelayTimeoutRef.current = null;
    }

    // Capture position immediately before timeout
    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 340; // Width of the popup
    const spacing = 16; // Space between card and popup

    // Calculate position (using fixed positioning, so don't add scrollY)
    let left = rect.right + spacing;
    let top = rect.top;

    // Check if popup would go off the right edge of the screen
    if (left + popupWidth > window.innerWidth) {
      // Position to the left of the card instead
      left = rect.left - popupWidth - spacing;
    }

    // Make sure popup doesn't go off screen on the left
    if (left < spacing) {
      left = spacing;
    }

    // Ensure popup doesn't go off the bottom of the screen
    const popupHeight = 400; // Approximate height
    if (top + popupHeight > window.innerHeight) {
      top = Math.max(spacing, window.innerHeight - popupHeight - spacing);
    }

    // Wait 0.5 seconds before showing popup
    hoverDelayTimeoutRef.current = setTimeout(() => {
      console.log('Popup position:', { top, left, manga: manga.title }); // Debug log
      setHoveredManga(manga);
      setPopupPosition({ top, left });

      // Auto-close after 5 seconds
      autoCloseTimeoutRef.current = setTimeout(() => {
        setHoveredManga(null);
        setPopupPosition(null);
      }, 5000);
    }, 500);
  };

  const handleMouseLeave = () => {
    // Clear hover delay if user leaves before popup shows
    if (hoverDelayTimeoutRef.current) {
      clearTimeout(hoverDelayTimeoutRef.current);
      hoverDelayTimeoutRef.current = null;
    }

    // Start timeout to close popup after 300ms
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredManga(null);
      setPopupPosition(null);
    }, 300);
  };

  const handlePopupMouseEnter = () => {
    // Clear close timeout when entering popup
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    // Clear auto-close timeout when entering popup
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    // Restart auto-close timer
    autoCloseTimeoutRef.current = setTimeout(() => {
      setHoveredManga(null);
      setPopupPosition(null);
    }, 5000);
  };

  const handlePopupMouseLeave = () => {
    // Close popup when leaving it
    setHoveredManga(null);
    setPopupPosition(null);
  };

  const handleCurrentReadingMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    // Clear any existing close timeout
    if (currentReadingCloseTimeoutRef.current) {
      clearTimeout(currentReadingCloseTimeoutRef.current);
      currentReadingCloseTimeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const buttonWidth = 180;
    const spacing = 16;

    let left = rect.right + spacing;
    let top = rect.top + (rect.height / 2) - 25; // Center vertically

    if (left + buttonWidth > window.innerWidth) {
      left = rect.left - buttonWidth - spacing;
    }

    setIsCurrentReadingHovered(true);
    setCurrentReadingPopupPosition({ top, left });
  };

  const handleCurrentReadingMouseLeave = () => {
    // Add delay before hiding to allow moving to the button
    currentReadingCloseTimeoutRef.current = setTimeout(() => {
      setIsCurrentReadingHovered(false);
      setCurrentReadingPopupPosition(null);
    }, 300);
  };

  const handleCurrentReadingPopupMouseEnter = () => {
    // Clear close timeout when entering popup
    if (currentReadingCloseTimeoutRef.current) {
      clearTimeout(currentReadingCloseTimeoutRef.current);
      currentReadingCloseTimeoutRef.current = null;
    }
  };

  const handleCurrentReadingPopupMouseLeave = () => {
    // Close popup when leaving it
    setIsCurrentReadingHovered(false);
    setCurrentReadingPopupPosition(null);
  };

  if (loading) {
    return (
      <div className="bento-item bento-manga">
        <div className="widget-content">
          <div className="manga-loading">Loading manga...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bento-item bento-manga">
      <div className="widget-content manga-widget">
        <div className="manga-header-section">
          <div className="manga-header-with-icon">
            <h3 className="manga-title">My 物語 (monogatari's)</h3>
          </div>
        </div>

        <div className="manga-content">
          {/* Currently Reading - Full Details */}
          {currentlyReading && (
            <div className="manga-featured-section">
              <div className="manga-featured-card">
                <div
                  className="manga-featured-cover"
                  onMouseEnter={handleCurrentReadingMouseEnter}
                  onMouseLeave={handleCurrentReadingMouseLeave}
                >
                  <img
                    src={currentlyReading.coverUrl}
                    alt={currentlyReading.title}
                    className="manga-cover"
                    loading="lazy"
                  />
                </div>
                <div className="manga-featured-info">
                  <div className="manga-featured-title-row">
                    <h3 className="manga-featured-title">
                      {currentlyReading.title}
                      <span className="manga-title-current"> (currently reading)</span>
                    </h3>
                    <p className="manga-featured-author">by {currentlyReading.author}</p>
                  </div>
                  <div className="manga-featured-meta">
                    {currentlyReading.year && (
                      <span className="manga-featured-year">{currentlyReading.year}</span>
                    )}
                    <span className="manga-featured-status">{currentlyReading.status}</span>
                    {currentlyReading.genres.map((genre) => (
                      <span key={genre} className="manga-featured-genre">{genre}</span>
                    ))}
                  </div>
                  <p className="manga-featured-description">{currentlyReading.description}</p>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          <div className="manga-section">
            <p className="manga-section-label">Recommendations</p>
            <div className="manga-grid-compact">
              {recommendations.map((manga) => (
                <div
                  key={manga.id}
                  className="manga-card-compact"
                  onMouseEnter={(e) => handleMouseEnter(manga, e)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="manga-cover-wrapper-compact">
                    <img
                      src={manga.coverUrl}
                      alt={manga.title}
                      className="manga-cover"
                      loading="lazy"
                    />
                  </div>
                  <p className="manga-card-title-compact">{manga.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Manga Popup - Rendered via Portal */}
      {hoveredManga && popupPosition && (() => {
        console.log('Rendering popup for:', hoveredManga.title, popupPosition);
        return ReactDOM.createPortal(
          <div
            className="manga-popup"
            style={{
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
            }}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
          >
            <h3 className="manga-popup-title">{hoveredManga.title}</h3>
            <p className="manga-popup-author">by {hoveredManga.author}</p>
            <div className="manga-popup-meta">
              {hoveredManga.year && (
                <span className="manga-popup-year">{hoveredManga.year}</span>
              )}
              <span className="manga-popup-status">{hoveredManga.status}</span>
            </div>
            <div className="manga-popup-genres">
              {hoveredManga.genres.map((genre) => (
                <span key={genre} className="manga-popup-genre">{genre}</span>
              ))}
            </div>
            <p className="manga-popup-description">{hoveredManga.description}</p>
            <a
              href={`https://mangadex.org/title/${hoveredManga.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="manga-popup-link"
            >
              <span className="manga-link-text">Read it on</span>
              <img
                src="/content/mangadex.svg"
                alt="MangaDex"
                className="mangadex-icon"
              />
              <img
                src="/content/MangaDex Wordmark.svg"
                alt="MangaDex"
                className="mangadex-wordmark"
              />
            </a>
          </div>,
          document.body
        );
      })()}

      {/* Currently Reading Hover Popup */}
      {isCurrentReadingHovered && currentReadingPopupPosition && currentlyReading && ReactDOM.createPortal(
        <a
          href={`https://mangadex.org/title/${currentlyReading.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="manga-current-reading-link"
          style={{
            top: `${currentReadingPopupPosition.top}px`,
            left: `${currentReadingPopupPosition.left}px`,
          }}
          onMouseEnter={handleCurrentReadingPopupMouseEnter}
          onMouseLeave={handleCurrentReadingPopupMouseLeave}
        >
          <span className="manga-link-text">Read it on</span>
          <img
            src="/content/mangadex.svg"
            alt="MangaDex"
            className="mangadex-icon"
          />
          <img
            src="/content/MangaDex Wordmark.svg"
            alt="MangaDex"
            className="mangadex-wordmark"
          />
        </a>,
        document.body
      )}
    </div>
  );
};

export default MangaTile;
